import { Link } from 'react-router';

import logoSvgInverted from '/assets/logo-inverted.svg';
import logoSvg from '/assets/logo.svg';
import type { ItemType } from '/imports/ui/components/Menu';
import { Menu } from '/imports/ui/components/Menu';
import { Icon } from '/imports/ui/foundation/Icons';
import { css, important, isLight, styled, useTheme } from '/imports/ui/foundation/Theme';

export interface TopNavProps<K extends string> {
  menu: Array<ItemType<K>>;
  selected: K | undefined;
}

export const TopNavApp = <K extends string>({ menu, selected }: TopNavProps<K>) => {
  const logoClassName = useLogoStyles();
  const popupClassName = useMenuPopupStyles();
  const { colorScheme } = useTheme();
  const logoPath = isLight(colorScheme) ? logoSvg : logoSvgInverted;

  return (
    <StyledMenu
      mode="horizontal"
      rootClassName={popupClassName}
      overflowedIndicator={<Icon name="bars" />}
      selectedKeys={selected != null ? [selected] : []}
      triggerSubMenuAction="click"
      items={[
        {
          key: 'cxLayoutOverview',
          className: logoClassName,
          label: (
            <Link to="/">
              <img src={logoPath} alt="Code Expert" />
            </Link>
          ),
        },
        ...menu,
      ]}
    />
  );
};

// -------------------------------------------------------------------------------------------------

const StyledMenu = styled(Menu, ({ tokens }) => ({
  background: 'none',
  borderBottom: 'none',
  fontSize: tokens.fontSizeLG,
  /* Align all items right of a `{ type: 'divider' }` menu item to the end */
  '.ant-menu-item-divider + *': {
    marginLeft: 'auto',
  },
  /* Increase the overflow menu item's click target */
  '.ant-menu-overflow-item-rest': {
    marginLeft: 'auto',
    paddingInline: 0,
    '& > *': {
      paddingInline: tokens.padding,
    },
  },
}));

/**
 * This targets the popup menu that is rendered in a React portal, so can't be targeted
 * by the menu's class hierarchy directly.
 */
const useMenuPopupStyles = css(() => ({
  '&.ant-menu-submenu': {
    '.ant-menu-item': {
      position: 'relative',
      ':is(a, button)::after': {
        /* Make the whole menu item a click target */
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        content: "' '",
      },
    },
  },
}));

const useLogoStyles = css(() => ({
  display: 'grid !important',
  placeItems: 'center',
  width: 234,
  marginLeft: important('8px'),
  marginRight: important('-6px'),

  img: {
    height: 50,
    position: 'relative',
    left: -10,
  },
}));
